export const supportedLocales = ["en", "ar"] as const;
export type Locale = (typeof supportedLocales)[number];
export type Direction = "ltr" | "rtl";
export type Message = string | Partial<Record<Intl.LDMLPluralRule, string>>;
export type Catalogs<L extends string = Locale> = Record<
  L,
  Record<string, Message>
>;
export type MessageValues = Record<string, string | number>;

export const directionFor = (locale: string): Direction =>
  locale === "ar" ? "rtl" : "ltr";

const intlLocale = (locale: string) => (locale === "ar" ? "ar-EG" : "en-EG");

const interpolate = (message: string, values: MessageValues = {}) =>
  message.replace(/\{([A-Za-z0-9_]+)\}/g, (placeholder, key: string) =>
    Object.hasOwn(values, key) ? String(values[key]) : placeholder,
  );

export function createI18n<L extends string>(locale: L, catalogs: Catalogs<L>) {
  return {
    locale,
    direction: directionFor(locale),
    t(key: string, values: MessageValues = {}): string {
      const message = catalogs[locale][key];
      if (!message) return `[${key}]`;
      if (typeof message === "string") return interpolate(message, values);
      const count = Number(values.count ?? 0);
      const plural = new Intl.PluralRules(locale).select(count);
      const selected = message[plural] ?? message.other;
      return selected ? interpolate(selected, values) : `[${key}]`;
    },
  };
}

const numberFormatter = (locale: string, options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(intlLocale(locale), options);

export const formatInteger = (locale: string, value: number) =>
  numberFormatter(locale, { maximumFractionDigits: 0 }).format(value);

export const formatNumber = (locale: string, value: number) =>
  numberFormatter(locale, { maximumFractionDigits: 2 }).format(value);

export const formatPercent = (locale: string, value: number) =>
  numberFormatter(locale, {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(value);

export const formatCurrency = (
  locale: string,
  value: number,
  currency = "EGP",
) => numberFormatter(locale, { style: "currency", currency }).format(value);

const dateFormatter = (locale: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(intlLocale(locale), { timeZone: "UTC", ...options });

export const formatDate = (locale: string, value: Date) =>
  dateFormatter(locale, { dateStyle: "medium" }).format(value);

export const formatDateTime = (locale: string, value: Date) =>
  dateFormatter(locale, { dateStyle: "medium", timeStyle: "short" }).format(
    value,
  );
